import { useState, useEffect, useCallback, useRef } from 'react';
import { isoToScheduleDateInput, parseScheduleDateInput } from '@/lib/scheduleUtils';

interface Props {
  value: string;
  onChange: (iso: string) => void;
  readOnly?: boolean;
  className?: string;
  placeholder?: string;
}

export default function ScheduleDateInput({
  value,
  onChange,
  readOnly = false,
  className = '',
  placeholder = 'DD/MM/YYYY',
}: Props) {
  const [text, setText] = useState(() => isoToScheduleDateInput(value));
  const datePickerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(isoToScheduleDateInput(value));
  }, [value]);

  const commit = useCallback(() => {
    if (!text.trim()) {
      onChange('');
      return;
    }
    const parsed = parseScheduleDateInput(text);
    if (parsed !== null) {
      onChange(parsed);
      setText(isoToScheduleDateInput(parsed));
    } else {
      setText(isoToScheduleDateInput(value));
    }
  }, [text, value, onChange]);

  const openCalendar = () => {
    if (readOnly) return;
    datePickerRef.current?.showPicker?.();
    datePickerRef.current?.focus();
  };

  return (
    <div className="relative flex items-center gap-1">
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        readOnly={readOnly}
        className={className}
      />
      {!readOnly && (
        <>
          <input
            ref={datePickerRef}
            type="date"
            value={value || ''}
            onChange={(e) => {
              const iso = e.target.value;
              if (iso) {
                onChange(iso);
                setText(isoToScheduleDateInput(iso));
              }
            }}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
          />
          <button
            type="button"
            onClick={openCalendar}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-[#6B7280] hover:bg-gray-50 hover:text-[#E8621A] cursor-pointer"
            title="Open calendar"
          >
            <i className="ri-calendar-line text-sm"></i>
          </button>
        </>
      )}
    </div>
  );
}
